// UI icons (research §2 "UI icons", same outline + fill routine as items; outline 2 px at 24 px):
// coin, cash, gear, lock, up, star, clock, speaker on/off, cart, hand, build, shop, merge-all.
import type { Ctx2D } from './ctx.ts';
import type { ArtPalette } from './palette.ts';
import { detailStroke, drawParts, fillP, type Part, part } from './passes.ts';
import {
  box,
  circle,
  line,
  type PathFn,
  regularPts,
  roundPoly,
  rrect,
  smoothPoly,
  starPts,
} from './shapes.ts';
import type { Generator, UiIconName, UiIconSpec } from './types.ts';

type Build = (
  pal: ArtPalette,
  P: (
    path: PathFn,
    tok: string,
    b: [number, number, number, number],
    extra?: Partial<Part>,
  ) => void,
  ink: string,
) => void;

const I: Record<UiIconName, Build> = {
  coin: (pal, P) =>
    P(circle(20, 20, 15), 'ui.money', [5, 5, 30, 30], {
      detail: (c) => {
        detailStroke(c, circle(20, 20, 10.5), pal.sw('ui.money').shade, 1.8);
        fillP(c, roundPoly(starPts(20, 20.5, 6, 2.8, 5), 0.6), pal.sw('ui.money').light);
      },
    }),
  cash: (pal, P) => {
    P(rrect(4, 10, 30, 18, 3), 'ui.buyDark', [4, 10, 30, 18]);
    P(rrect(7, 14, 30, 18, 3), 'ui.buy', [7, 14, 30, 18], {
      detail: (c) => {
        fillP(c, circle(22, 23, 5), pal.sw('ui.buy').shade);
        fillP(c, rrect(18, 14, 5, 18, 0), pal.hex('ui.cream2'));
      },
    });
  },
  gear: (pal, P) => {
    const pts = regularPts(20, 20, 16, 16).map(
      ([x, y], i) =>
        [20 + (x - 20) * (i % 2 ? 0.78 : 1), 20 + (y - 20) * (i % 2 ? 0.78 : 1)] as const,
    );
    P(roundPoly(pts, 1.4), 'mach.top', [4, 4, 32, 32], {
      detail: (c) => fillP(c, circle(20, 20, 5.2), pal.sw('mach.top').outline),
    });
  },
  lock: (pal, P) => {
    P(
      (c) => {
        c.moveTo(11, 20);
        c.arc(20, 14, 9, Math.PI, 0);
        c.lineTo(29, 20);
        c.lineTo(25, 20);
        c.lineTo(25, 14);
        c.arc(20, 14, 5, 0, Math.PI, true);
        c.lineTo(15, 20);
        c.closePath();
      },
      'material.steel',
      [11, 5, 18, 15],
    );
    P(rrect(8, 18, 24, 18, 4), 'ui.brass', [8, 18, 24, 18], {
      detail: (c) => {
        fillP(c, circle(20, 25, 2.6), pal.sw('ui.brass').outline);
        fillP(c, rrect(19, 26, 2, 5, 1), pal.sw('ui.brass').outline);
      },
    });
  },
  up: (_pal, P) =>
    P(
      roundPoly(
        [
          [20, 4],
          [35, 20],
          [26, 20],
          [26, 35],
          [14, 35],
          [14, 20],
          [5, 20],
        ],
        2,
      ),
      'ui.buy',
      [5, 4, 30, 31],
    ),
  star: (_pal, P) => P(roundPoly(starPts(20, 21, 16, 7.5, 5), 1.6), 'ui.money', [4, 5, 32, 31]),
  clock: (pal, P, ink) =>
    P(circle(20, 20, 15), 'ui.cream', [5, 5, 30, 30], {
      outline: pal.hex('ui.brassDark'),
      detail: (c) => {
        detailStroke(
          c,
          line([
            [20, 20],
            [20, 10],
          ]),
          ink,
          2.6,
        );
        detailStroke(
          c,
          line([
            [20, 20],
            [27, 23],
          ]),
          ink,
          2.6,
        );
        fillP(c, circle(20, 20, 2), pal.hex('ui.error'));
      },
    }),
  speakerOn: (pal, P, ink) => speaker(pal, P, ink, true),
  speakerOff: (pal, P, ink) => speaker(pal, P, ink, false),
  cart: (pal, P) => {
    P(
      roundPoly(
        [
          [6, 10],
          [34, 10],
          [30, 26],
          [11, 26],
        ],
        2.5,
      ),
      'mach.top',
      [6, 10, 28, 16],
      {
        detail: (c) =>
          detailStroke(
            c,
            line([
              [14, 16],
              [30, 16],
            ]),
            pal.sw('mach.top').shade,
            1.6,
          ),
      },
    );
    P(circle(13, 32, 3.4), 'mach.front', [9.6, 28.6, 6.8, 6.8]);
    P(circle(28, 32, 3.4), 'mach.front', [24.6, 28.6, 6.8, 6.8]);
  },
  hand: (pal, P) => {
    P(rrect(16, 3, 8, 22, 4), 'ui.cream', [16, 3, 8, 22]);
    P(
      roundPoly(
        [
          [10, 18],
          [30, 16],
          [33, 26],
          [28, 37],
          [14, 37],
          [9, 28],
        ],
        4,
      ),
      'ui.cream',
      [9, 16, 24, 21],
      {
        detail: (c) => {
          for (const x of [22, 27])
            detailStroke(
              c,
              line([
                [x, 18],
                [x, 23],
              ]),
              pal.sw('ui.cream').outline,
              1.2,
            );
        },
      },
    );
  },
  build: (_pal, P) => {
    P(
      roundPoly(
        [
          [18, 17],
          [23, 17],
          [23, 37],
          [18, 37],
        ],
        2,
      ),
      'ui.wood',
      [18, 17, 5, 20],
    );
    P(rrect(8, 6, 26, 12, 3), 'material.steel', [8, 6, 26, 12]);
  },
  shop: (pal, P) => {
    P(rrect(7, 18, 26, 18, 2), 'ui.cream2', [7, 18, 26, 18], {
      detail: (c) => fillP(c, rrect(16, 24, 8, 12, 1), pal.hex('ui.woodDark')),
    });
    P(
      (c) => {
        c.moveTo(4, 12);
        c.lineTo(36, 12);
        c.lineTo(36, 18);
        for (let i = 4; i > 0; i--) c.arc(4 + i * 8 - 4, 18, 4, 0, Math.PI);
        c.closePath();
      },
      'machine.gallery',
      [4, 12, 32, 10],
      {
        detail: (c) => {
          for (const x of [12, 28]) fillP(c, rrect(x - 4, 12, 8, 6, 0), pal.hex('ui.cream'));
        },
      },
    );
    P(rrect(4, 6, 32, 7, 2), 'ui.woodDark', [4, 6, 32, 7]);
  },
  mergeAll: (pal, P) => {
    P(rrect(4, 16, 14, 14, 3), 'tier.3', [4, 16, 14, 14]);
    P(rrect(22, 16, 14, 14, 3), 'tier.3', [22, 16, 14, 14]);
    P(roundPoly(starPts(20, 12, 9, 4, 4, 0), 1), 'glow.merge', [11, 3, 18, 18], {
      detail: (c) => fillP(c, circle(20, 12, 2), pal.sw('glow.merge').hi),
    });
  },
};

function speaker(pal: ArtPalette, P: Parameters<Build>[1], ink: string, on: boolean): void {
  P(
    roundPoly(
      [
        [5, 14],
        [12, 14],
        [21, 6],
        [21, 34],
        [12, 26],
        [5, 26],
      ],
      1.6,
    ),
    'mach.top',
    [5, 6, 16, 28],
  );
  const draw = (c: Ctx2D) => {
    if (on) {
      for (const r of [7, 12]) {
        c.beginPath();
        c.arc(22, 20, r, -0.8, 0.8);
        c.strokeStyle = ink;
        c.lineWidth = 3;
        c.lineCap = 'round';
        c.stroke();
      }
    } else {
      detailStroke(
        c,
        line([
          [26, 14],
          [36, 26],
        ]),
        pal.hex('ui.error'),
        3.4,
      );
      detailStroke(
        c,
        line([
          [36, 14],
          [26, 26],
        ]),
        pal.hex('ui.error'),
        3.4,
      );
    }
  };
  P(smoothPoly([]), 'mach.top', [0, 0, 0, 0], { noOutline: true, flat: true, detail: draw });
}

export const uiIconGen: Generator<UiIconSpec> = (c, spec, px, env) => {
  const { pal } = env;
  const S = spec.w * 100;
  c.save();
  c.scale(px / 100, px / 100);
  c.scale(S / 40, S / 40);
  const ow = Math.max(
    pal.art.uiIconOutline * 40,
    (((env.sprites.outlineMinPx * env.dpr) / px) * 100 * 40) / S,
  );
  const parts: Part[] = [];
  const build = I[spec.icon];
  build(
    pal,
    (path, tok, [x, y, w, h], extra = {}) =>
      parts.push(part(path, pal.sw(tok), box(x, y, w, h), extra)),
    pal.hex('ui.ink'),
  );
  drawParts(c, parts, {
    ow,
    rim: false,
    halo: null,
    glow: null,
    shadeBand: pal.rules.shadeBand,
    hiW: 1.6,
  });
  c.restore();
};
